import { createColumnHelper } from '@tanstack/react-table'
import {
  AccountOwnerPill,
  ContactTagCell,
  DistributorsCell,
  StagePill,
  TaglistCell,
} from '../../FbUI/StagePill'
import dayjs from 'dayjs'
import { Deal } from '../../../models/deals'
import {
  formatInteger,
  formatTwoDecimal,
  formatUsd,
  formatUsdDecimal,
} from 'utils/formatting'
import RowExpandArrow from 'components/DataTable/DataTable/DataTableRow/RowExpandArrow'
import s from 'styled-components'
import { FbLink } from 'components/FbUI/FbLink'
import apiService from 'services/api'
import { useMemo } from 'react'
import { IColumnSort } from 'components/DataTable/types'
import { getCompanyUrl } from 'utils/getCompanyUrl'
import { usePreferences } from 'context/preferences/PreferencesContext'

const CompanyContainer = s.div`
  display: flex;
  align-items: center;
`

const CompanyExpandArrow = s.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  margin-right: 10px;
`

export const useDealsTableCols = (
  showCompanyColumn: boolean,
  sorting?: IColumnSort<object>[]
) => {
  const api = apiService()
  const { preferences } = usePreferences()
  const { data: categories } = api.useGetCompanyCategoriesAndSub()

  const tooltips: Record<string, unknown> = preferences?.tooltips || {}

  const columnHelper = createColumnHelper<Deal>()

  const cols = useMemo(
    () => [
      ...(showCompanyColumn
        ? [
            columnHelper.accessor('contact_company', {
              id: 'company',
              meta: { tooltip: tooltips?.['deals_company'] },
              header: 'Company',
              cell: (info) => {
                const row = info.row.original

                return (
                  <CompanyContainer>
                    <CompanyExpandArrow>
                      <RowExpandArrow />
                    </CompanyExpandArrow>
                    <FbLink
                      to={`${getCompanyUrl(row.contact_company)}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {row.contact_company?.name}
                    </FbLink>
                  </CompanyContainer>
                )
              },
              size: 350,
            }),
          ]
        : []),
      columnHelper.accessor('company_size', {
        id: 'company_size',
        meta: { tooltip: tooltips?.['deals_chain_size'] },
        header: 'Size',
        size: 100,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      columnHelper.accessor('contact_company.category', {
        id: 'category',
        header: 'Category',
        cell: (info) => {
          return (
            categories?.find(
              (a) => a.id === (info.getValue() as unknown as number)
            )?.name ?? '-'
          )
        },
      }),
      columnHelper.accessor('contact_company.sub_category', {
        id: 'sub_category',
        header: 'Type',
        cell: (info) => {
          const value = info.getValue()
          let name = '-'

          categories?.forEach((a) => {
            a.sub_categories.forEach((b) => {
              if (b.id === (value as unknown as number)) {
                name = b.name
              }
            })
          })

          return name
        },
      }),
      columnHelper.accessor('product', {
        id: 'product',
        meta: { tooltip: tooltips?.['deals_product'] },
        header: 'Product',
        size: 250,

        cell: (info) => {
          const row = info.row.original
          return row.product?.name
        },
      }),
      columnHelper.accessor('sales_stage', {
        id: 'sales_stage',
        meta: { tooltip: tooltips?.['deals_sales_stage'] },
        header: 'Sales Stage',
        size: 250,
        cell: (info) => {
          if (!info.row?.original?.sales_stage) return <>-</>
          return <StagePill stageId={info.row.original?.sales_stage} />
        },
      }),
      columnHelper.accessor('last_datetime_stage_changed', {
        id: 'last_datetime_stage_changed',
        meta: { tooltip: tooltips?.['deals_last_datetime_stage_changed'] },
        header: 'Days In Stage',
        size: 150,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${dayjs().diff(dayjs(value), 'day')} days`
        },
      }),
      columnHelper.accessor('expected_days_to_close', {
        meta: { tooltip: tooltips?.['deals_expected_days_to_close'] },
        size: 150,
        header: 'Expected Close Date',
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${dayjs().add(value, 'day').format('MMM DD, YYYY')}`
        },
      }),
      columnHelper.accessor('close_date_override', {
        meta: { tooltip: tooltips?.['deals_close_date_override'] },
        header: 'Close Date (override)',
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return dayjs(value).format('MMM DD, YYYY')
        },
      }),

      columnHelper.accessor('account_owner', {
        meta: { tooltip: tooltips?.['deals_account_owner'] },
        header: 'Account Owner',
        size: 250,
        cell: (info) => {
          return <AccountOwnerPill account_owner_id={info.getValue()} />
        },
      }),

      columnHelper.accessor('predicted_annual_total', {
        id: 'predicted_annual_total',
        meta: { tooltip: tooltips?.['deals_predicted_total'] },
        header: '1yr Total Value',
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatUsd(value)
        },
      }),

      columnHelper.accessor('predicted_annual_brand_opp', {
        id: 'predicted_annual_brand_opp',
        meta: { tooltip: tooltips?.['predicted_annual_brand_opp'] },
        header: '1yr Brand Value',
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatUsd(value)
        },
      }),
      columnHelper.accessor('annual_revenue_opportunity', {
        id: 'annual_revenue_opportunity',
        meta: { tooltip: tooltips?.['deals_annual_revenue_opportunity'] },
        header: 'Annual Revenue Opportunity',
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatUsd(value)
        },
      }),
      columnHelper.accessor('predicted_annual_revenue', {
        id: 'predicted_annual_revenue',
        meta: { tooltip: tooltips?.['deals_predicted_annual_revenue'] },
        header: '1yr Revenue Value',
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatUsd(value)
        },
      }),
      columnHelper.accessor('predicted_monthly_revenue', {
        id: 'predicted_monthly_revenue',
        meta: { tooltip: tooltips?.['deals_predicted_monthly_revenue'] },
        header: 'Monthly Revenue',
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatUsd(value)
        },
      }),
      columnHelper.accessor('monthly_revenue_override', {
        id: 'monthly_revenue_override',
        meta: { tooltip: tooltips?.['deals_monthly_revenue_override'] },
        header: 'Monthly Revenue (override)',
        size: 185,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatUsdDecimal(value)
        },
      }),

      columnHelper.accessor('predicted_annual_volume', {
        id: 'predicted_annual_volume',
        meta: { tooltip: tooltips?.['deals_predicted_annual_volume'] },
        header: '1yr Volume (lbs)',
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatInteger(value ?? 0)} lbs`
        },
      }),
      columnHelper.accessor('predicted_monthly_volume', {
        id: 'predicted_monthly_volume',
        meta: { tooltip: tooltips?.['deals_predicted_monthly_volume'] },
        header: 'Monthly Volume',
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatInteger(value ?? 0)} lbs`
        },
      }),
      columnHelper.accessor('monthly_volume_override', {
        id: 'monthly_volume_override',
        header: 'Monthly Volume (override)',
        meta: { tooltip: tooltips?.['deals_monthly_volume_override'] },
        size: 175,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return `${formatTwoDecimal(value ?? 0)} lbs`
        },
      }),

      columnHelper.accessor('contact_company_tags', {
        meta: { tooltip: tooltips?.['deals_contact_company_tags'] },
        header: 'Tags',
        size: 260,
        cell: (info) => {
          return (
            <TaglistCell taglist={info.row.original?.contact_company.taglist} />
          )
        },
      }),

      columnHelper.accessor('contact_tags', {
        meta: { tooltip: tooltips?.['deals_contact_tags'] },
        header: 'Contact Labels',
        size: 260,
        cell: (info) => {
          return <ContactTagCell deals={[info.row.original]} />
        },
      }),

      columnHelper.accessor('contact_company.distributors', {
        meta: { tooltip: tooltips?.['deals_distributors'] },
        header: 'Known Distributors',
        size: 260,
        cell: (info) => {
          return (
            <DistributorsCell
              distributors={info.getValue()}
              asc={
                sorting?.length && sorting[0].id === 'distributors'
                  ? !sorting[0].desc
                  : undefined
              }
            />
          )
        },
      }),
      columnHelper.accessor('contact_company.note_count', {
        id: 'note_count',
        meta: { tooltip: tooltips?.['deals_note_count'] },
        header: 'Note Count',
        size: 110,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      columnHelper.accessor('contact_company.contact_count', {
        id: 'contact_count',
        meta: { tooltip: tooltips?.['deals_contact_count'] },
        header: 'Contact Count',
        size: 110,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return <>-</>
          return formatInteger(value)
        },
      }),
      columnHelper.accessor('contact_company.days_since_last_modified', {
        id: 'days_since_last_modified',
        meta: { tooltip: tooltips?.['dealspipeline_days_since_last_modified'] },
        header: 'Days Since Last Update',
        cell: (info) => {
          return info.getValue()
        },
      }),
      columnHelper.accessor('modified', {
        meta: { tooltip: tooltips?.['deals_modified'] },
        header: 'Last Modified',
        size: 150,
        cell: (info) => {
          return dayjs(info.getValue()).format('MM/DD/YYYY')
        },
      }),
      columnHelper.accessor('created', {
        meta: { tooltip: tooltips?.['deals_created'] },
        header: 'Date Created',
        size: 150,
        cell: (info) => dayjs(info.getValue()).format('MM/DD/YYYY'),
      }),
    ],
    [categories, preferences, sorting, showCompanyColumn]
  )

  return cols
}
